import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AltalabLogo } from "../components/AltalabLogo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  ShoppingBag,
  Star,
  Shield,
  MapPin,
  Clock,
  Share2,
  Instagram,
  Twitter,
  ArrowLeft,
  Loader2,
  MessageCircle,
  Check,
  Copy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons mapping
const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    telegram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    privacy: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none"/>
      </svg>
    ),
    onlyfans: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8z"/>
      </svg>
    ),
    twitter: <Twitter className={className} />,
    instagram: <Instagram className={className} />,
    tiktok: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
  };
  return icons[platform] || <ShoppingBag className={className} />;
};

const ServicePage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [orderMessage, setOrderMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchService();
  }, [serviceId]);

  const fetchService = async () => {
    try {
      const response = await axios.get(`${API}/services/public/${serviceId}`);
      setService(response.data);
    } catch (error) {
      console.error("Failed to fetch service:", error);
      toast.error("Serviço não encontrado");
    } finally {
      setLoading(false);
    }
  };

  const getPhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  const handleOrderClick = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      setShowOrderModal(true);
    }
  };

  const handleSubmitOrder = async () => {
    if (!user || !token) {
      setShowAuthModal(true);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/services/${serviceId}/order?Authorization=Bearer ${token}`,
        { service_id: serviceId, message: orderMessage }
      );
      toast.success("Pedido enviado! O vendedor entrará em contato.");
      setShowOrderModal(false);
      setOrderMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar pedido");
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: service?.title,
          text: `Confira esse serviço: ${service?.title} - R$ ${service?.price?.toFixed(2)}`,
          url: url,
        });
      } catch (error) {
        // User cancelled sharing or browser doesn't support - silent fail is expected
        console.debug("Share cancelled or not supported:", error.message);
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-[#080808] text-white flex flex-col items-center justify-center p-4">
        <ShoppingBag className="w-16 h-16 text-white/20 mb-4" />
        <h1 className="text-2xl font-bold mb-2">Serviço não encontrado</h1>
        <p className="text-white/50 mb-6">Este serviço pode ter sido removido ou está indisponível.</p>
        <Button onClick={() => navigate("/marketplace")}>
          Ver outros serviços
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/marketplace")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <AltalabLogo size="small" />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="text-white/70"
              data-testid="share-btn"
            >
              {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Share2 className="w-5 h-5" />}
            </Button>
            {!user && (
              <Button
                size="sm"
                className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white"
                onClick={() => navigate("/auth?register=true")}
              >
                Criar Conta
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-32 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Seller Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-4 mb-8"
          >
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-white/10 flex-shrink-0">
              {getPhoto(service.seller?.profile_photo) ? (
                <img 
                  src={getPhoto(service.seller?.profile_photo)} 
                  alt={service.seller?.display_name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30">
                  <Users className="w-10 h-10" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold">{service.seller?.display_name}</h2>
                {service.seller?.is_verified && (
                  <Shield className="w-5 h-5 text-emerald-400" />
                )}
              </div>
              <p className="text-white/50 text-sm mb-2">@{service.seller?.username}</p>
              {service.seller?.trust_score > 0 && (
                <div className="flex items-center gap-1 text-yellow-400">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="font-medium">{service.seller?.trust_score?.toFixed(1)}</span>
                  <span className="text-white/30 text-sm">({service.total_reviews || 0} avaliações)</span>
                </div>
              )}
              {service.seller?.location?.city && (
                <div className="flex items-center gap-1 text-white/50 text-sm mt-1">
                  <MapPin className="w-4 h-4" />
                  <span>{service.seller?.location?.city}, {service.seller?.location?.state}</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Service Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-8 mb-6"
          >
            {/* Platform Badge */}
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-[#FD5564]/20 text-[#FD5564]">
                <PlatformIcon platform={service.platform} className="w-6 h-6" />
              </div>
              <Badge variant="outline" className="text-white/70 border-white/20 uppercase">
                {service.platform}
              </Badge>
              <Badge variant="outline" className="text-white/70 border-white/20">
                {service.service_type}
              </Badge>
            </div>

            {/* Title & Description */}
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{service.title}</h1>
            <p className="text-white/70 mb-6">{service.description}</p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-white/50 text-sm mb-1">Alcance</div>
                <div className="text-lg font-semibold">{service.reach}</div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-white/50 text-sm mb-1">Entrega</div>
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <Clock className="w-4 h-4 text-white/50" />
                  {service.delivery_time}
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4">
                <div className="text-white/50 text-sm mb-1">Pedidos</div>
                <div className="text-lg font-semibold">{service.total_orders || 0}</div>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#FD5564]/20 to-[#FF7854]/10 rounded-xl">
              <div>
                <div className="text-white/50 text-sm">Preço</div>
                <div className="text-3xl font-bold text-[#FD5564]">
                  R$ {service.price?.toFixed(2).replace('.', ',')}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Other Services */}
          {service.other_services?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6"
            >
              <h3 className="text-lg font-semibold mb-4">Outros serviços de {service.seller?.display_name}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {service.other_services.map((otherService) => (
                  <div
                    key={otherService.id}
                    onClick={() => navigate(`/s/${otherService.id}`)}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 cursor-pointer hover:border-[#FD5564]/50 transition-all"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <PlatformIcon platform={otherService.platform} className="w-4 h-4 text-[#FD5564]" />
                      <span className="text-sm text-white/70 uppercase">{otherService.platform}</span>
                    </div>
                    <div className="font-medium text-sm line-clamp-1 mb-1">{otherService.title}</div>
                    <div className="text-[#FD5564] font-bold">
                      R$ {otherService.price?.toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Reviews */}
          {service.reviews?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-lg font-semibold mb-4">Avaliações</h3>
              <div className="space-y-3">
                {service.reviews.map((review, index) => (
                  <div key={index} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-white/20'}`}
                          />
                        ))}
                      </div>
                      <span className="text-white/50 text-sm">{review.reviewer_name}</span>
                    </div>
                    <p className="text-white/70">{review.comment}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#080808]/95 backdrop-blur-md border-t border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div>
            <div className="text-white/50 text-sm">Total</div>
            <div className="text-2xl font-bold text-[#FD5564]">
              R$ {service.price?.toFixed(2).replace('.', ',')}
            </div>
          </div>
          <Button
            size="lg"
            className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white px-8"
            onClick={handleOrderClick}
            data-testid="order-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Quero Contratar
          </Button>
        </div>
      </div>

      {/* Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Solicitar Serviço</DialogTitle>
            <DialogDescription className="text-white/50">
              Envie uma mensagem para {service.seller?.display_name} sobre este serviço.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-white/5 rounded-xl p-4">
              <div className="font-medium mb-1">{service.title}</div>
              <div className="text-[#FD5564] font-bold">
                R$ {service.price?.toFixed(2).replace('.', ',')}
              </div>
            </div>
            <Textarea
              placeholder="Deixe uma mensagem para o vendedor (opcional)..."
              value={orderMessage}
              onChange={(e) => setOrderMessage(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[100px]"
            />
            <Button
              className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
              onClick={handleSubmitOrder}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Enviar Pedido"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Crie uma conta para continuar</DialogTitle>
            <DialogDescription className="text-white/50">
              Para contratar este serviço, você precisa ter uma conta na ALTALAB.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
              onClick={() => navigate(`/auth?register=true&redirect=/s/${serviceId}`)}
            >
              Criar Conta Grátis
            </Button>
            <Button
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/10"
              onClick={() => navigate(`/auth?redirect=/s/${serviceId}`)}
            >
              Já tenho conta - Entrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicePage;
